import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, viloyatUsersAPI as usersAPI, youthAPI, problemsAPI, migrantsAPI, eventsAPI, questionsAPI } from '../services/api';
import Icon from '../components/Icon';
import { Button } from '../components/UI';

function Donut({ data, size = 120, thickness = 20, center }) {
  const r = (size - thickness) / 2, circ = 2 * Math.PI * r;
  const total = data.reduce((s, d) => s + d.value, 0);
  let offset = 0;
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={thickness} />
        {data.filter(d => d.value > 0).map((d, i) => {
          const dash = (d.value / (total||1)) * circ;
          const el = <circle key={i} cx={size/2} cy={size/2} r={r} fill="none" stroke={d.color} strokeWidth={thickness-2}
            strokeDasharray={`${Math.max(0,dash-2)} ${circ-Math.max(0,dash-2)}`} strokeDashoffset={-offset} strokeLinecap="round" style={{transition:'all .8s'}}/>;
          offset += dash; return el;
        })}
      </svg>
      {center && <div style={{ position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center' }}>{center}</div>}
    </div>
  );
}

function Gauge({ value, max, label, color, size=110 }) {
  const pct = max > 0 ? Math.round(value/max*100) : 0;
  const circ = Math.PI * 48;
  return (
    <div style={{ textAlign:'center' }}>
      <svg width={size} height={65} viewBox="0 0 120 68">
        <path d="M 12 60 A 48 48 0 0 1 108 60" fill="none" stroke="#f1f5f9" strokeWidth="9" strokeLinecap="round"/>
        <path d="M 12 60 A 48 48 0 0 1 108 60" fill="none" stroke={color} strokeWidth="9" strokeLinecap="round"
          strokeDasharray={`${circ}`} strokeDashoffset={circ-(circ*Math.min(pct,100)/100)} style={{transition:'stroke-dashoffset 1s'}}/>
        <text x="60" y="50" textAnchor="middle" style={{fontSize:20,fontWeight:900,fill:color}}>{pct}%</text>
      </svg>
      <div style={{ fontSize:10,fontWeight:600,color:'#64748b',marginTop:-2 }}>{label}</div>
    </div>
  );
}

function ModuleCard({ icon, title, stats, color, bg, onClick }) {
  return (
    <div onClick={onClick} style={{ background:'#fff',borderRadius:16,border:'1px solid #e2e8f0',padding:'18px 20px',cursor:'pointer',transition:'all .2s',position:'relative',overflow:'hidden' }}
      onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 8px 30px rgba(0,0,0,0.08)';}}
      onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='none';}}>
      <div style={{ position:'absolute',top:-10,right:-10,width:60,height:60,borderRadius:'50%',background:bg,opacity:0.3 }}/>
      <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:12,position:'relative' }}>
        <div style={{ width:38,height:38,borderRadius:12,background:bg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18 }}>{icon}</div>
        <div style={{ fontSize:13,fontWeight:700,color:'#1e293b' }}>{title}</div>
        <Icon name="chevronRight" size={14} color="#cbd5e1" style={{marginLeft:'auto'}} />
      </div>
      <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
        {stats.map((s,i) => (
          <div key={i} style={{ flex:1,minWidth:50,textAlign:'center',padding:'6px 4px',borderRadius:8,background:s.bg||'#f8fafc' }}>
            <div style={{ fontSize:16,fontWeight:800,color:s.color||'#1e293b' }}>{s.value}</div>
            <div style={{ fontSize:9,color:'#94a3b8',fontWeight:500 }}>{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [ys, setYs] = useState({});
  const [ps, setPs] = useState({});
  const [ms, setMs] = useState({});
  const [es, setEs] = useState({});
  const [qs, setQs] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      dashboardAPI.stats().then(r => setStats(r.data)),
      usersAPI.list().then(r => setUsers(r.data.results || r.data)),
      youthAPI.stats().then(r => setYs(r.data)),
      problemsAPI.stats().then(r => setPs(r.data)),
      migrantsAPI.stats().then(r => setMs(r.data)),
      eventsAPI.stats().then(r => setEs(r.data)),
      questionsAPI.stats().then(r => setQs(r.data)),
    ]).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  if (loading) return (
    <div style={{textAlign:'center',padding:100}}>
      <div style={{display:'inline-block',width:36,height:36,border:'4px solid #e0e7ff',borderTopColor:'#6366f1',borderRadius:'50%',animation:'spin 1s linear infinite'}}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  const userList = Array.isArray(users) ? users : [];
  const empRate = ys.total > 0 ? Math.round((ys.employed||0)/ys.total*100) : 0;
  const probSolveRate = ps.total > 0 ? Math.round(((ps.hal_qilindi||0)+(ps.yopildi||0))/ps.total*100) : 0;
  const qAnswerRate = qs.total > 0 ? Math.round((qs.javob_berildi||0)/qs.total*100) : 0;

  return (
    <div>
      {/* ===== HERO ===== */}
      <div style={{
        background:'linear-gradient(135deg, #1e1b4b 0%, #3730a3 40%, #6366f1 100%)',
        borderRadius:24,padding:'28px 32px',marginBottom:24,position:'relative',overflow:'hidden'
      }}>
        <div style={{position:'absolute',top:-50,right:-30,width:200,height:200,borderRadius:'50%',background:'radial-gradient(circle,rgba(99,102,241,0.3) 0%,transparent 70%)'}}/>
        <div style={{position:'absolute',bottom:-30,left:'40%',width:140,height:140,borderRadius:'50%',background:'radial-gradient(circle,rgba(139,92,246,0.2) 0%,transparent 70%)'}}/>
        <div style={{position:'relative'}}>
          <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:16}}>
            <div style={{width:50,height:50,borderRadius:16,background:'rgba(255,255,255,0.15)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:24}}>🏛</div>
            <div>
              <h2 style={{fontSize:20,fontWeight:900,color:'#fff',margin:0}}>Boshqaruv paneli</h2>
              <p style={{fontSize:12,color:'#c7d2fe',margin:0}}>Viloyat bo'yicha umumiy ko'rsatkichlar</p>
            </div>
          </div>
          {/* Hero stats */}
          <div className="hero-row" style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:8}}>
            {[
              {v:ys.total||0,l:'Yoshlar',e:'👥',c:'#fff'},
              {v:ps.total||0,l:'Muammolar',e:'📋',c:ps.overdue>0?'#fca5a5':'#fff'},
              {v:ms.total_migrants||0,l:'Migrantlar',e:'✈️',c:'#fff'},
              {v:es.total||0,l:'Tadbirlar',e:'🎯',c:'#fff'},
              {v:qs.total||0,l:'Savollar',e:'💬',c:'#fff'},
              {v:stats.total||0,l:'Tuman adminlar',e:'🏢',c:'#fff'},
            ].map(s=>(
              <div key={s.l} style={{padding:'10px',borderRadius:12,background:'rgba(255,255,255,0.08)',border:'1px solid rgba(255,255,255,0.1)',textAlign:'center'}}>
                <div style={{fontSize:15}}>{s.e}</div>
                <div style={{fontSize:18,fontWeight:900,color:s.c,lineHeight:1.2}}>{s.v}</div>
                <div style={{fontSize:9,color:'#a5b4fc',fontWeight:500}}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== GAUGES ===== */}
      <div className="dash-g3" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16,marginBottom:20}}>
        <div style={{background:'#fff',borderRadius:16,border:'1px solid #e2e8f0',padding:'16px',textAlign:'center'}}>
          <Gauge value={empRate} max={100} label="Bandlik darajasi" color={empRate>50?'#22c55e':empRate>30?'#f59e0b':'#ef4444'} />
          <div style={{fontSize:10,color:'#94a3b8',marginTop:4}}>{ys.employed||0}/{ys.total||0} band</div>
        </div>
        <div style={{background:'#fff',borderRadius:16,border:'1px solid #e2e8f0',padding:'16px',textAlign:'center'}}>
          <Gauge value={probSolveRate} max={100} label="Muammo hal qilish" color={probSolveRate>60?'#22c55e':probSolveRate>30?'#f59e0b':'#ef4444'} />
          <div style={{fontSize:10,color:'#94a3b8',marginTop:4}}>{(ps.hal_qilindi||0)+(ps.yopildi||0)}/{ps.total||0} hal</div>
        </div>
        <div style={{background:'#fff',borderRadius:16,border:'1px solid #e2e8f0',padding:'16px',textAlign:'center'}}>
          <Gauge value={qAnswerRate} max={100} label="Savolga javob" color={qAnswerRate>60?'#22c55e':qAnswerRate>30?'#f59e0b':'#ef4444'} />
          <div style={{fontSize:10,color:'#94a3b8',marginTop:4}}>{qs.javob_berildi||0}/{qs.total||0} javob</div>
        </div>
      </div>

      {/* ===== CHARTS ROW ===== */}
      <div className="dash-g2" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:20}}>
        {/* Bandlik tarkibi */}
        <div style={{background:'#fff',borderRadius:16,border:'1px solid #e2e8f0',padding:'20px 24px'}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:16}}>
            <span style={{fontSize:16}}>💼</span>
            <span style={{fontSize:14,fontWeight:700,color:'#1e293b'}}>Yoshlar bandlik tarkibi</span>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:20}}>
            <Donut size={130} thickness={22} data={[
              {value:ys.employed||0,color:'#22c55e'},
              {value:ys.unemployed||0,color:'#ef4444'},
              {value:ys.student||0,color:'#3b82f6'},
              {value:ys.entrepreneur||0,color:'#f59e0b'},
              {value:Math.max(0,(ys.total||0)-(ys.employed||0)-(ys.unemployed||0)-(ys.student||0)-(ys.entrepreneur||0)),color:'#94a3b8'},
            ]} center={<><div style={{fontSize:22,fontWeight:900}}>{ys.total||0}</div><div style={{fontSize:9,color:'#94a3b8'}}>jami</div></>} />
            <div style={{flex:1}}>
              {[
                {l:'Ishlamoqda',v:ys.employed||0,c:'#22c55e'},
                {l:'Ishsiz',v:ys.unemployed||0,c:'#ef4444'},
                {l:"O'qimoqda",v:ys.student||0,c:'#3b82f6'},
                {l:'Tadbirkor',v:ys.entrepreneur||0,c:'#f59e0b'},
              ].map(x=>(
                <div key={x.l} style={{display:'flex',alignItems:'center',gap:6,padding:'4px 0'}}>
                  <span style={{width:8,height:8,borderRadius:'50%',background:x.c,flexShrink:0}}/>
                  <span style={{fontSize:11,color:'#64748b',flex:1}}>{x.l}</span>
                  <span style={{fontSize:12,fontWeight:800,color:x.c}}>{x.v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Ijtimoiy holatlar */}
        <div style={{background:'#fff',borderRadius:16,border:'1px solid #e2e8f0',padding:'20px 24px'}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:16}}>
            <span style={{fontSize:16}}>🔍</span>
            <span style={{fontSize:14,fontWeight:700,color:'#1e293b'}}>Ijtimoiy ko'rsatkichlar</span>
          </div>
          {[
            {l:'Migrantlar',v:ms.total_migrants||0,max:ys.total||1,c:'#f59e0b',i:'✈️'},
            {l:'Profilaktik hisobda',v:ys.prev_registered||0,max:ys.total||1,c:'#ef4444',i:'🔒'},
            {l:'Kam ta\'minlangan',v:ys.low_income||0,max:ys.total||1,c:'#ca8a04',i:'🏠'},
            {l:'Migrant: shartnomali',v:ms.with_contract||0,max:ms.total_migrants||1,c:'#22c55e',i:'📄'},
            {l:"Muddati o'tgan muammolar",v:ps.overdue||0,max:ps.total||1,c:'#dc2626',i:'⏰'},
          ].map(b=>{
            const pct = b.max>0?(b.v/b.max)*100:0;
            return (
              <div key={b.l} style={{marginBottom:10}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                  <span style={{fontSize:11,fontWeight:600,color:'#475569'}}>{b.i} {b.l}</span>
                  <span style={{fontSize:12,fontWeight:800,color:b.c}}>{b.v}</span>
                </div>
                <div style={{height:8,borderRadius:4,background:'#f1f5f9',overflow:'hidden'}}>
                  <div style={{height:'100%',borderRadius:4,width:`${pct}%`,background:`linear-gradient(90deg,${b.c}88,${b.c})`,transition:'width .8s'}}/>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== MODULE CARDS ===== */}
      <div style={{marginBottom:20}}>
        <div style={{fontSize:14,fontWeight:700,color:'#1e293b',marginBottom:12,display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:16}}>📊</span> Modullar bo'yicha ko'rsatkichlar
        </div>
        <div className="dash-g3" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:14}}>
          <ModuleCard icon="📋" title="Muammolar" color="#8b5cf6" bg="#f5f3ff" onClick={()=>navigate('/problems')}
            stats={[
              {value:ps.yangi||0,label:'Yangi',color:'#6366f1',bg:'#eef2ff'},
              {value:ps.jarayonda||0,label:'Jarayonda',color:'#ca8a04',bg:'#fffbeb'},
              {value:ps.hal_qilindi||0,label:'Hal qilindi',color:'#16a34a',bg:'#f0fdf4'},
              {value:ps.overdue||0,label:"Kechikkan",color:'#dc2626',bg:'#fef2f2'},
            ]} />
          <ModuleCard icon="✈️" title="Migrantlar" color="#f59e0b" bg="#fffbeb" onClick={()=>navigate('/migrants')}
            stats={[
              {value:ms.gone||0,label:'Ketgan',color:'#dc2626',bg:'#fef2f2'},
              {value:ms.abroad||0,label:'Chetda',color:'#ca8a04',bg:'#fffbeb'},
              {value:ms.returned||0,label:'Qaytgan',color:'#16a34a',bg:'#f0fdf4'},
              {value:ms.with_contract||0,label:'Shartnomal',color:'#2563eb',bg:'#dbeafe'},
            ]} />
          <ModuleCard icon="🎯" title="Tadbirlar" color="#22c55e" bg="#f0fdf4" onClick={()=>navigate('/events')}
            stats={[
              {value:es.rejada||0,label:'Rejada',color:'#6366f1',bg:'#eef2ff'},
              {value:es.faol||0,label:'Faol',color:'#ca8a04',bg:'#fffbeb'},
              {value:es.tugadi||0,label:'Tugadi',color:'#16a34a',bg:'#f0fdf4'},
              {value:es.total_participants||0,label:'Ishtirok',color:'#0ea5e9',bg:'#e0f2fe'},
            ]} />
          <ModuleCard icon="💬" title="Hokimga savol" color="#6366f1" bg="#eef2ff" onClick={()=>navigate('/hokim')}
            stats={[
              {value:qs.yuborildi||0,label:'Yuborildi',color:'#6366f1',bg:'#eef2ff'},
              {value:qs.oqildi||0,label:"O'qildi",color:'#ca8a04',bg:'#fffbeb'},
              {value:qs.javob_berildi||0,label:'Javob',color:'#16a34a',bg:'#f0fdf4'},
              {value:qs.urgent||0,label:'Shoshilinch',color:'#dc2626',bg:'#fef2f2'},
            ]} />
          <ModuleCard icon="👥" title="Yoshlar reestri" color="#3b82f6" bg="#dbeafe" onClick={()=>navigate('/youth')}
            stats={[
              {value:ys.male||0,label:'Erkak',color:'#2563eb',bg:'#dbeafe'},
              {value:ys.female||0,label:'Ayol',color:'#db2777',bg:'#fce7f3'},
              {value:ys.disabled||0,label:'Nogiron',color:'#64748b',bg:'#f1f5f9'},
              {value:ys.orphan||0,label:'Yetim',color:'#ca8a04',bg:'#fffbeb'},
            ]} />
          <ModuleCard icon="🧠" title="Sun'iy intellekt" color="#7c3aed" bg="#f5f3ff" onClick={()=>navigate('/ai')}
            stats={[
              {value:ys.prev_registered||0,label:'Profilaktik',color:'#dc2626',bg:'#fef2f2'},
              {value:ys.crime_convicted||0,label:'Jinoyatchi',color:'#b91c1c',bg:'#fef2f2'},
              {value:ys.migrant||0,label:'Migrant',color:'#ca8a04',bg:'#fffbeb'},
              {value:ys.low_income||0,label:'Kam tamin.',color:'#f59e0b',bg:'#fffbeb'},
            ]} />
        </div>
      </div>

      {/* ===== RECENT USERS ===== */}
      <div style={{borderRadius:16,background:'#fff',border:'1px solid #e2e8f0',overflow:'hidden'}}>
        <div style={{padding:'16px 24px',borderBottom:'1px solid #f1f5f9',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <span style={{fontSize:16}}>🏢</span>
            <h3 style={{fontSize:14,fontWeight:700,margin:0}}>Tuman/Shahar administratorlari</h3>
          </div>
          <Button variant="primary" size="sm" onClick={()=>navigate('/users')}>Barchasini ko'rish</Button>
        </div>
        {userList.length === 0 ? (
          <div style={{padding:40,textAlign:'center',color:'#94a3b8'}}>Ma'lumot topilmadi</div>
        ) : userList.slice(0,5).map((u,i)=>(
          <div key={u.id} style={{padding:'12px 24px',borderBottom:i<Math.min(userList.length,5)-1?'1px solid #f1f5f9':'none',display:'flex',alignItems:'center',gap:14,transition:'background .15s'}}
            onMouseEnter={e=>e.currentTarget.style.background='#fafbff'}
            onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
            <div style={{width:36,height:36,borderRadius:10,background:'linear-gradient(135deg,#6366f1,#8b5cf6)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,color:'#fff'}}>
              {u.first_name?.[0]}{u.last_name?.[0]}
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600,color:'#1e293b'}}>{u.first_name} {u.last_name}</div>
              <div style={{fontSize:11,color:'#94a3b8'}}>{u.role_display}{u.tuman?` • ${u.tuman}`:''}</div>
            </div>
            <span style={{fontSize:10,fontWeight:600,padding:'3px 10px',borderRadius:20,background:u.is_active_user?'#dcfce7':'#fee2e2',color:u.is_active_user?'#16a34a':'#ef4444'}}>
              {u.is_active_user?'Faol':'Bloklangan'}
            </span>
          </div>
        ))}
      </div>

      <style>{`
        .hero-row{grid-template-columns:repeat(6,1fr)}
        .dash-g3{grid-template-columns:repeat(3,1fr)}
        .dash-g2{grid-template-columns:1fr 1fr}
        @media(max-width:1100px){.hero-row{grid-template-columns:repeat(3,1fr)}.dash-g3{grid-template-columns:repeat(2,1fr)}}
        @media(max-width:768px){.dash-g2{grid-template-columns:1fr}.dash-g3{grid-template-columns:1fr}.hero-row{grid-template-columns:repeat(2,1fr)}}
      `}</style>
    </div>
  );
}
